<template>
  <div>
    <jodit-editor
      ref="joditEditor"
      :value="content"
      :config="editorConfig"
      @input="handleInput"
      @change="handleChange"
    />
  </div>
</template>

<script>
/* eslint-disable no-console */
import { JoditEditor } from "jodit-vue";
import "jodit/build/jodit.min.css";

const BASE_API_URL = `${process.env.VUE_APP_SERVER_URI}`;

export default {
  props: {
    value: {
      type: String,
      default: "",
    },
    autoConvertPaste: {
      type: Boolean,
      default: false,
    },
    config: {
      type: Object,
      default: () => ({
        height: 500,
        toolbarAdaptive: false,
        enter: "P",
        defaultActionOnPaste: "insert_clear_html",
        uploader: {
          insertImageAsBase64URI: false,
          url: `${BASE_API_URL}/iskal/api/file/static/image`,
          method: "POST",
          filesVariableName: "file",
          prepareData: (formData) => {
            return formData;
          },
          headers: {
            Authorization: `Bearer ${this.$store.getters["auth/authToken"]}`,
          },
          process: (resp) => {
            const baseurl = `${BASE_API_URL}/static/`;
            const imageUrl = `${baseurl}${resp.path}`;
            return {
              files: [imageUrl],
              path: imageUrl,
            };
          },
          error: (e) => {
            // eslint-disable-next-line no-console
            console.error("업로드 실패:", e);
          },
        },
        buttons: [
          "bold",
          "italic",
          "underline",
          "strikethrough",
          "|",
          "superscript",
          "subscript",
          "|",
          "ul",
          "ol",
          "|",
          "indent",
          "outdent",
          "|",
          "align",
          "|",
          "table",
          "|",
          "link",
          "image",
          "|",
          "undo",
          "redo",
          "|",
        ],
      }),
    },
  },
  components: {
    JoditEditor,
  },
  data() {
    return {
      content: this.value || "",
      editorConfig: {
        ...this.config,
      },
    };
  },
  mounted() {
    if (this.autoConvertPaste) {
      this.setupPasteHandler();
    }
  },
  methods: {
    setupPasteHandler() {
      // Jodit 에디터가 준비될 때까지 폴링
      const maxAttempts = 20;
      let attempts = 0;

      const trySetupPaste = () => {
        attempts++;
        const editorComponent = this.$refs.joditEditor;

        // jodit-vue에서는 editor 속성으로 접근
        let editor = null;
        if (editorComponent) {
          editor = editorComponent.editor || editorComponent.$editor;
        }

        if (editor && editor.events) {
          console.log("Paste handler 설정 완료");

          editor.events.on("paste", (event) => {
            const clipboardData = event.clipboardData || window.clipboardData;
            if (!clipboardData) return;

            const pastedText = clipboardData.getData("text/plain");
            const pastedHtml = clipboardData.getData("text/html");

            // HTML이 아닌 일반 텍스트가 붙여넣기된 경우에만 변환
            if (pastedText && !pastedHtml) {
              const convertedHtml = this.convertTextToHtml(pastedText);
              if (convertedHtml) {
                event.preventDefault();
                editor.s.insertHTML(convertedHtml);
                return false;
              }
            }
          });
        } else if (attempts < maxAttempts) {
          // 100ms 후 재시도
          setTimeout(trySetupPaste, 100);
        } else {
          console.warn("Jodit 에디터 인스턴스를 찾을 수 없습니다.");
        }
      };

      this.$nextTick(trySetupPaste);
    },

    // 텍스트를 HTML로 변환 (위계 자동 인식)
    convertTextToHtml(text) {
      const lines = text.replace(/\r\n?/g, "\n").split("\n");
      const html = [];
      let paragraphBuf = [];
      let inList = false;
      let inBlockquote = false;
      let blockquoteBuf = [];

      const escapeHtml = (s) => {
        return s
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;")
          .replace(/'/g, "&#039;");
      };

      // 헤딩 인식: 1. / 1.1 / 1.1.1 등
      const parseHeading = (line) => {
        const trimmed = line.trim();
        const m = trimmed.match(/^(\d+(?:\.\d+)*\.?)\s+(.+)$/);
        if (!m) return null;

        const numbering = m[1];
        const title = m[2].trim();

        if (title.length === 0 || title.length > 100) return null;

        // 문장 끝 패턴 제외
        const sentenceEnd = /(\.|다\.?|니다\.?|요\.?|한다\.?|하였다\.?|\?|!|;)$/;
        if (sentenceEnd.test(title)) return null;

        const depth = numbering.replace(/\.$/, "").split(".").length;

        // depth 1 = H3 (장), depth 2 = H4 (절), depth 3 = H5 (소절), depth 4+ = H6
        let level = 3;
        if (depth === 2) level = 4;
        else if (depth === 3) level = 5;
        else if (depth >= 4) level = 6;

        return { level, numbering, title };
      };

      // 글머리표 인식
      const parseBullet = (line) => {
        const m = line.trimStart().match(/^([•\-*])\s+(.+)$/);
        if (!m) return null;
        return { text: m[2].trim() };
      };

      const flushParagraph = () => {
        if (paragraphBuf.length === 0) return;
        const pText = paragraphBuf.join(" ").trim();
        if (pText) {
          html.push(`<p>${escapeHtml(pText)}</p>`);
        }
        paragraphBuf = [];
      };

      const flushBlockquote = () => {
        if (blockquoteBuf.length === 0) return;
        const qText = blockquoteBuf.join("<br>");
        html.push(`<blockquote>${qText}</blockquote>`);
        blockquoteBuf = [];
        inBlockquote = false;
      };

      const openList = () => {
        if (inList) return;
        flushParagraph();
        flushBlockquote();
        html.push("<ul>");
        inList = true;
      };

      const closeList = () => {
        if (!inList) return;
        html.push("</ul>");
        inList = false;
      };

      // 본문 처리
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const trimmed = line.trim();

        // 빈 줄
        if (trimmed === "") {
          flushParagraph();
          closeList();
          flushBlockquote();
          continue;
        }

        // 인용문 (> 로 시작)
        if (trimmed.startsWith(">")) {
          closeList();
          flushParagraph();
          inBlockquote = true;
          blockquoteBuf.push(escapeHtml(trimmed.substring(1).trim()));
          continue;
        } else if (inBlockquote) {
          flushBlockquote();
        }

        // 글머리표
        const bullet = parseBullet(line);
        if (bullet) {
          openList();
          html.push(`<li>${escapeHtml(bullet.text)}</li>`);
          continue;
        } else {
          closeList();
        }

        // 헤딩
        const heading = parseHeading(trimmed);
        if (heading) {
          flushParagraph();
          closeList();
          const tag = `h${heading.level}`;
          html.push(`<${tag}>${escapeHtml(heading.numbering)} ${escapeHtml(heading.title)}</${tag}>`);
          continue;
        }

        // 일반 본문
        paragraphBuf.push(trimmed);
      }

      // 마무리
      closeList();
      flushParagraph();
      flushBlockquote();

      return html.join("\n");
    },

    handleInput(newContent) {
      this.updateContent(newContent);
    },
    handleChange(newContent) {
      this.updateContent(newContent);
    },
    updateContent(newContent) {
      if (newContent !== this.content) {
        this.content = newContent || "";
        this.$emit("input", this.content);
        this.$emit("change", this.content);
      }
    },
  },
  watch: {
    value(newValue) {
      if (newValue !== this.content) {
        this.content = newValue || "";
      }
    },
  },
};
</script>

<style lang="scss" scoped>
:deep(.jodit-container) {
  border-radius: 4px;
}

:deep(.jodit-workplace) {
  border-radius: 0 0 4px 4px;
}

:deep(.jodit-toolbar__box) {
  border-radius: 4px 4px 0 0;
}
</style>
